import CanvasUtils from './CanvasUtils'

/**
 * 地平盘相位圈相较普通盘额外增大的半径比例。
 * 地平盘（固定环模式）画面上的圆圈描边只有三处：相位圈、24 山向环的内沿与外沿。
 * 该增量同时供「相位圈绘制」与「地平盘 ASC/MC 轴线内端」使用，避免两处各写一套魔数后再脱钩。
 */
const HORIZON_PHASE_CIRCLE_DELTA = 0.05

/**
 * 星盘绘制工具类
 * 封装所有星盘绘制逻辑，直接使用渲染器实例
 */
export default class AstrolabeDrawer {
  constructor (renderer) {
    // 创建并持有渲染器实例
    this.renderer = renderer
    this.ctx = this.renderer.ctx
    this.signInfo = { // 有用了，在劫夺的时候用
      signName: ['戌', '酉', '申', '未', '午', '巳', '辰', '卯', '寅', '丑', '子', '亥']
    }
    this.planetPositionsLookup = []
    this.aspectPositions = [] // 清空相位位置数组
    this.starPositions = [] // 新增：存储恒星位置信息
    this.specialAxisPositions = [] // 新增：存储特殊轴(ASC/MC)位置信息
    this.pointsReal = {} // 新增：存储特殊点数据
    this.houses = [] // 新增：存储宫位数据用于等分宫位计算
    this.ehFlag = false // 等分宫位开关，true=使用等分宫位绘制，false=使用标准绘制
    this.signLabelPositions = [] // 新增：存储宫头星座标签位置
    this.houseSectorPositions = [] // 新增：存储后天宫扇形位置
    this.tianyunPositions = [] // 新增：存储天运位置信息

    this.fixWord = ['子', '癸', '丑', '艮', '寅', '甲', '卯', '乙', '辰', '巽', '巳', '丙', '午', '丁', '未', '坤', '申', '庚', '酉', '辛', '戌', '乾', '亥', '壬']
    this.styles = {
      signCircle: {
        innerRadiusRatio: 0.78,
        outerRadiusRatio: 0.9,
        strokeStyle: '#34495e',
        evenFill: '#fff',
        oddFill: '#fff',
        lineWidth: 1,
        font: 'bold 26px Segoe UI',
        degFont: '18px 楷体',
        fontColor: [
          'rgba(253, 10, 0, 1)', // 戌
          'rgba(30, 91, 0, 1)', // 酉
          'rgba(0, 0, 255, 1)', // 申
          'rgba(159, 0, 116, 1)', // 未
          'rgba(255, 0, 0, 1)', // 午
          'rgba(30, 91, 0, 1)', // 巳
          'rgba(0, 0, 255, 1)', // 辰
          'rgba(159, 0, 116, 1)', // 卯
          'rgba(255, 0, 0, 1)', // 寅
          'rgba(30, 91, 0, 1)', // 丑
          'rgba(0, 0, 255, 1)', // 子
          'rgba(159, 0, 116, 1)' // 亥
        ],
        signColor: [
          'rgba(224, 247, 250, 1)', // 戌
          'rgba(224, 247, 250, 1)', // 酉
          'rgba(224, 247, 250, 1)', // 申
          'rgba(224, 247, 250, 1)', // 未
          'rgba(224, 247, 250, 1)', // 午
          'rgba(224, 247, 250, 1)', // 巳
          'rgba(224, 247, 250, 1)', // 辰
          'rgba(224, 247, 250, 1)', // 卯
          'rgba(224, 247, 250, 1)', // 寅
          'rgba(224, 247, 250, 1)', // 丑
          'rgba(224, 247, 250, 1)', // 子
          'rgba(224, 247, 250, 1)' // 亥
        ]
      },
      outerRing: {
        innerRadiusRatio: 0.78,
        outerRadiusRatio: 0.9,
        strokeStyle: '#34495e',
        evenFill: '#f8f9fa',
        oddFill: '#e9ecef',
        lineWidth: 1,
        font: 'bold 26px Segoe UI',
        fontColor: [
          'rgba(0, 0, 255, 1)', // 子 - 蓝（对应12宫子）
          'rgba(255, 0, 0, 1)', // 癸 - 红（与前一个子不同，用红）
          'rgba(30, 91, 0, 1)', // 丑 - 绿（对应12宫丑）
          'rgba(0, 0, 255, 1)', // 艮 - 蓝（与前一个丑不同，用蓝）
          'rgba(255, 0, 0, 1)', // 寅 - 红（对应12宫寅）
          'rgba(159, 0, 116, 1)', // 甲 - 紫（与前一个寅不同，用紫）
          'rgba(159, 0, 116, 1)', // 卯 - 紫（对应12宫卯）
          'rgba(30, 91, 0, 1)', // 乙 - 绿（与前一个卯不同，用绿）
          'rgba(0, 0, 255, 1)', // 辰 - 蓝（对应12宫辰）
          'rgba(159, 0, 116, 1)', // 巽 - 紫（与前一个辰不同，用紫）
          'rgba(30, 91, 0, 1)', // 巳 - 绿（对应12宫巳）
          'rgba(255, 0, 0, 1)', // 丙 - 红（与前一个巳不同，用红）
          'rgba(255, 0, 0, 1)', // 午 - 红（对应12宫午）
          'rgba(0, 0, 255, 1)', // 丁 - 蓝（与前一个午不同，用蓝）
          'rgba(159, 0, 116, 1)', // 未 - 紫（对应12宫未）
          'rgba(30, 91, 0, 1)', // 坤 - 绿（与前一个未不同，用绿）
          'rgba(0, 0, 255, 1)', // 申 - 蓝（对应12宫申）
          'rgba(159, 0, 116, 1)', // 庚 - 紫（与前一个申不同，用紫）
          'rgba(30, 91, 0, 1)', // 酉 - 绿（对应12宫酉）
          'rgba(255, 0, 0, 1)', // 辛 - 红（与前一个酉不同，用红）
          'rgba(253, 10, 0, 1)', // 戌 - 橙红（对应12宫戌）
          'rgba(159, 0, 116, 1)', // 乾 - 紫（与前一个戌不同，用紫）
          'rgba(159, 0, 116, 1)', // 亥 - 紫（对应12宫亥）
          'rgba(0, 0, 255, 1)' // 壬 - 蓝（与前一个亥不同，用蓝）
        ]
      },
      constellations: {
        innerRadiusRatio: 0.76 - 0.00,
        outerRadiusRatio: 0.78,
        fillColor: '#fff',
        strokeStyle: '#34495e',
        lineWidth: 0.5
      },
      houseSector: {
        innerRadiusRatio: 0.45,
        outerRadiusRatio: 0.78 - 0.02,
        evenFill: '#fff',
        oddFill: '#fff',
        fillColor: '#fff',
        strokeStyle: '#34495e',
        lineWidth: 0.5
      },
      // 本命盘外圈样式（最外圈）- 用于黄道盘
      natalHouseSector: {
        innerRadiusRatio: 0.9,
        outerRadiusRatio: 1.025, // 将本命盘外圈宽度收窄到原来约一半
        evenFill: 'rgba(74, 111, 165, 0.1)', // 淡蓝色背景
        oddFill: 'rgba(74, 111, 165, 0.1)', // 淡蓝色背景
        strokeStyle: '#4a6fa5', // 深蓝色边框
        lineWidth: 1,
        // 行星文本样式（与黄道盘一致，半径在内外圈范围内）
        planetNameFont: '20px 楷体',
        textRadiusRatio: 0.98, // 行星文字位置同步收窄
        speedRadiusRatio: 1.14, // 速度位置
        degTextRadiusRatio: 1.06, // 度数位置
        degFont: '12px Arial',
        signNameTextRadiusRatio: 1.02, // 星座名位置
        signFont: '16px 楷体',
        minTextRadiusRatio: 0.98, // 分位置
        planetRadiusRatio: 0.92, // 行星圆点位置（在0.9-1.15范围内）
        directionRadiusRatio: 0.94 // 逆行位置
      },
      houseLabel: {
        fillStyle: '#2c3e50',
        font: '12px Arial'
      },
      planet: {
        planetNameFont: '20px 楷体',
        midpointNameFont: '16px 楷体', // 中点符号字体小两号
        planetRadiusRatio: 0.46,
        speedRadiusRatio: 0.74,
        textRadiusRatio: 0.70,
        degTextRadiusRatio: 0.64,
        degFont: '12px Arial',
        signNameTextRadiusRatio: 0.59,
        signFont: '16px 楷体',
        minTextRadiusRatio: 0.54,
        directionRadiusRatio: 0.49,
        speedFont: '10px Arial',
        lineWidth: 1
      },
      star: {
        starNameFont: '14px 楷体',
        textSpacing: 16, // 字符间距（像素）
        fontColor: '#000000',
        dotColor: '#FFD700',
        dotRadius: 2,
        dotFont: '14px Arial',
        starRadiusRatio: 0.46,
        textRadiusRatio: 0.70
      },
      phaseCircle: {
        radiusRatio: 0.41,
        strokeStyle: '#34495e',
        evenFill: '#fff',
        oddFill: '#fff',
        lineWidth: 1
      },
      phaseLine: {
        strokeStyle: '#9b59b6',
        lineWidth: 1,
        colors: [
          '#FF0000', // 0度
          '#00ffff', // 30度 - 湖蓝色
          '#cc12e5', // 45度 - 紫色
          '#0000FF', // 60度 - 蓝色
          '#00ffff', // 72度 - 湖蓝色
          '#FF0000', // 90度 - 红色
          '#0000FF', // 120度 - 蓝色
          '#cc12e5', // 135度 - 紫色
          '#00ffff', // 144度 - 湖蓝色
          '#cc12e5', // 150度 - 紫色
          '#FF0000' // 180度 - 红色
        ]
      },
      phaseSymbol: {
        font: '16px Arial',
        offset: 0, // 符号偏移距离
        gapSize: 16, // 符号周围不画线的间隙大小
        colors: [
          '#FF0000', // 0度
          '#00ffff', // 30度 - 湖蓝色
          '#cc12e5', // 45度 - 紫色
          '#0000FF', // 60度 - 蓝色
          '#00ffff', // 72度 - 湖蓝色
          '#FF0000', // 90度 - 红色
          '#0000FF', // 120度 - 蓝色
          '#cc12e5', // 135度 - 紫色
          '#00ffff', // 144度 - 湖蓝色
          '#cc12e5', // 150度 - 紫色
          '#FF0000' // 180度 - 红色
        ]
      },
      radialLine: {
        innerRadiusRatio: 0.45,
        outerRadiusRatio: 0.9,
        strokeStyle: '#34495e',
        lineWidth: 1
      },
      radialText: {
        fillStyle: '#9b59b6',
        font: '22px 楷体'
      },
      // ASC(红)/MC(蓝) 特殊轴线。
      // 长度严格限制在「宫位圈」（houseSector: 0.45 ~ 0.76）之内，不得超出该圈：
      // 内端若小于 0.45 会戳进相位圈(0.41)与宫位数字(≈0.43)那一带。
      // 注：这两个通用比例只服务黄道盘（宫位圈外侧有描边）；
      // 地平盘没有宫位圈，其轴线端点另按相位圈与外环内沿对齐，见 drawHorizonSpecialAxes()。
      axisArrow: {
        vcolor: '#0000ff',
        hcolor: '#ff0000',
        fontColor: '#ff0000',
        lineWidth: 3,
        arrowSize: 15,
        font: '16px Arial',
        outerRadiusRatio: 0.76, // 宫位圈外沿（= houseSector.outerRadiusRatio）
        innerRadiusRatio: 0.45 // 宫位圈内沿（= houseSector.innerRadiusRatio）
      },
      planetDot: {
        fillStyle: '#9b59b6',
        radius: 2
      }
    }

    // 新增：刻度样式
    this.scaleStyles = {
      show: false,
      outerRadiusRatio: 0.9,
      largeTickLength: 15,
      smallTickLength: 8,
      tickColor: '#666',
      textColor: '#333',
      textFont: '12px Arial',
      textOffset: 25
    }

    // 添加定时器相关变量
    this.msTimer = null
    this.msTimerInterval = 500 // 默认0.5秒
    this.msTimerCallback = null
    this.msTimerRunning = false
    this.flinkStatus = false

    this.refreshAlarmStar = false // 只刷报警恒星
  }

  /**
   * 将地平盘方位角（顺时针角度）转换为逆时针绘制角度
   * 由于 zeroAngle 已设置为 270（0点在正下方），只需将顺时针角度转逆时针
   * 转换公式：绘制角度 = 360 - 方位角
   *
   * @param {number} azimuth - 地平盘方位角（顺时针角度，0-360）
   * @returns {number} 转换后的绘制角度（逆时针系统，0-360）
   */
  convertAzimuthToDrawAngle (azimuth) {
    if (azimuth === undefined || azimuth === null) {
      return 0
    }
    // 顺时针角度转逆时针：360 - 方位角
    return CanvasUtils.normalizeAngle(360 - azimuth)
  }

  /**
   * 清空位置数组
   */
  clearPositions () {
    this.planetPositionsLookup = []
    this.aspectPositions = []
    this.starPositions = []
    this.constellationSectors = []
    this.specialAxisPositions = []
    this.signLabelPositions = []
    this.houseSectorPositions = []
    this.tianyunPositions = []
  }

  /**
   * 设置等分宫位开关
   * @param {boolean} flag - true=使用等分宫位绘制，false=使用标准绘制
   */
  setEhFlag (flag) {
    this.ehFlag = !!flag
  }

  /**
   * 获取主盘 ASC/MC 轴线使用的显示角度。
   * 等分宫位模式下，ASC 固定在显示坐标 0 度，MC 与 ASC 保持 90 度直角关系。
   * @param {Object} specialPoints - 特殊点数据
   * @returns {Object} ASC/MC 显示角度
   */
  getSpecialAxisDisplayAngles (specialPoints = {}) {
    const hasAsc = specialPoints.ASC !== undefined
    const hasMc = specialPoints.MC !== undefined
    const asc = this.ehFlag ? 0 : (hasAsc ? specialPoints.ASC : 0)
    const mc = this.ehFlag && (hasMc || hasAsc)
      ? CanvasUtils.normalizeAngle(asc - 90)
      : (hasMc ? specialPoints.MC : undefined)

    return {
      ASC: hasAsc ? asc : undefined,
      MC: hasMc ? mc : undefined
    }
  }

  /**
   * 使用与特殊轴相同的显示角度准备 ASC/MC 行星。
   * 这是仅针对显示坐标的覆盖，不改变绝对黄经等原始数据。
   * @param {Array} planets - 行星数据
   * @param {Object} specialPoints - 特殊点数据
   * @returns {Array} 准备后的行星数据
   */
  preparePlanetsWithSpecialAxisAngles (planets = [], specialPoints = {}) {
    const axisAngles = this.getSpecialAxisDisplayAngles(specialPoints)

    return (Array.isArray(planets) ? planets : []).map(planet => {
      if (!planet) {
        return planet
      }
      if (planet.index === 1000015 && axisAngles.ASC !== undefined) {
        return { ...planet, drawPos: axisAngles.ASC }
      }
      if (planet.index === 1000014 && axisAngles.MC !== undefined) {
        return { ...planet, drawPos: axisAngles.MC }
      }
      return { ...planet }
    })
  }

  /**
   * 根据位置计算等分宫位中的绘制角度（无houseNumber时使用）
   * @param {number} position - 原始位置
   * @param {Array} houses - 可选的宫位数据，默认使用 this.houses
   * @returns {number} 等分宫位中的绘制角度
   */
  calcEqualHouseAngleByPosition (position, houses = null) {
    const houseData = houses || this.houses
    if (!houseData || houseData.length === 0) {
      return position
    }

    // 找到position所在的宫位
    let houseIndex = 0
    for (let i = 0; i < 12; i++) {
      const houseStart = houseData[i].position
      const houseEnd = houseData[(i + 1) % 12].position

      // 处理跨越0度的情况
      if (houseEnd > houseStart) {
        if (position >= houseStart && position < houseEnd) {
          houseIndex = i
          break
        }
      } else {
        // 跨越0度
        if (position >= houseStart || position < houseEnd) {
          houseIndex = i
          break
        }
      }
    }

    const houseStart = houseData[houseIndex].position
    const houseEnd = houseData[(houseIndex + 1) % 12].position

    // 计算宫位大小
    let houseSize = houseEnd - houseStart
    if (houseSize < 0) houseSize += 360

    // 计算对象在宫内的偏移
    let objOffset = position - houseStart
    if (objOffset < 0) objOffset += 360

    // 计算相对位置 (0-1)
    const relativePos = objOffset / houseSize

    // 返回等分宫位中的绘制角度
    return houseIndex * 30 + relativePos * 30
  }

  initDrawer (canvasSize) {
    // 清空画布
    this.renderer.ctx.clearRect(0, 0, canvasSize, canvasSize)

    // 清空位置数组
    this.clearPositions()

    // 绘制基础圆环
    this.renderer.drawBaseCircle()

    // // 新增：绘制刻度圈（在最底层）
    // this.drawScaleCircle()
  }

  /**
   * 绘制星座背景（先天宫扇形）
   * @param {Object} pointsReal - 特殊点数据
   */
  drawSignSectors (pointsReal) {
    // 绘制先天宫格子
    for (let i = 0; i < 12; i++) {
      // 计算先天宫角度（星座位置）
      const startAngleSign = CanvasUtils.normalizeAngle((i * 30 + 360 - pointsReal.ASC) % 360)
      const endAngleSign = CanvasUtils.normalizeAngle((((i + 1) % 12) * 30 + 360 - pointsReal.ASC) % 360)

      // 1. 绘制先天宫扇形（星座背景）
      this.renderer.drawSector(
        startAngleSign,
        endAngleSign,
        this.styles.signCircle.innerRadiusRatio,
        this.styles.signCircle.outerRadiusRatio,
        this.styles.signCircle.signColor[i],
        this.styles.signCircle.strokeStyle,
        this.styles.signCircle.lineWidth,
        false // 是否绘制直边
      )
    }
  }

  /**
   * 绘制28星宿格子
   * @param {Object} pointsReal - 特殊点数据
   */
  drawConstellationsSector (xingxiuPositions, pointsReal) {
    this.constellationSectors = [] // 清空之前的位置记录

    // 绘制28星宿格子
    for (let i = 0; i < xingxiuPositions.length; i++) {
      const startAngle = CanvasUtils.normalizeAngle(xingxiuPositions[i].drawPos)
      const endAngle = CanvasUtils.normalizeAngle(xingxiuPositions[(i + 1) % xingxiuPositions.length].drawPos)

      // 记录扇环信息
      const sector = {
        index: i,
        startAngle,
        endAngle,
        innerRadius: this.renderer.radius * this.styles.constellations.innerRadiusRatio,
        outerRadius: this.renderer.radius * this.styles.constellations.outerRadiusRatio,
        // 不再记录 absoluteLongitude：它原先只用于悬浮框显示黄经，而黄经已从悬浮框移除（盘面已按黄经落位）
        xingxiuName: xingxiuPositions[i].xingxiuName,
        signName: xingxiuPositions[i].xingxiuInSignName,
        signOffDeg: xingxiuPositions[i].signOffDeg,
        signOffMin: xingxiuPositions[i].signOffMin,
        signOffSec: xingxiuPositions[i].signOffSec
      }
      this.constellationSectors.push(sector)

      const xingxiuColor = xingxiuPositions[i].xingxiuColor

      this.renderer.drawSector(
        startAngle,
        endAngle,
        this.styles.constellations.innerRadiusRatio,
        this.styles.constellations.outerRadiusRatio,
        xingxiuColor,
        this.styles.constellations.strokeStyle,
        this.styles.constellations.lineWidth,
        true // 是否绘制直边
      )
    }
  }

  /**
   * 绘制宫位背景（后天宫扇形）
   * @param {number} startAngle - 起始角度
   * @param {number} endAngle - 结束角度
   * @param {Object} styleConfig - 可选的样式配置，默认使用 houseSector
   */
  drawHouseSector (startAngle, endAngle, styleConfig = null) {
    const styles = styleConfig || this.styles.houseSector
    this.renderer.drawSector(
      startAngle,
      endAngle,
      styles.innerRadiusRatio,
      styles.outerRadiusRatio,
      styles.evenFill,
      styles.strokeStyle,
      styles.lineWidth,
      true // 绘制直边
    )
  }

  /**
   * 绘制宫头星座标签
   * @param {number} startAngle - 起始角度
   * @param {Object} house - 宫位数据
   * @param {number} signIndex - 星座索引
   */
  drawSignLabels (startAngle, house, signIndex) {
    const signLabelRadius = this.renderer.radius * (this.styles.signCircle.outerRadiusRatio + this.styles.signCircle.innerRadiusRatio) / 2
    const labelPosition = CanvasUtils.getPoint(
      startAngle,
      signLabelRadius,
      this.renderer.center,
      this.renderer.zeroAngle
    )
    const shouldFlip = CanvasUtils.isAngleInFirstOrSecondQuadrant(startAngle)

    // 绘制星座名称
    this.renderer.drawHouseLabel(
      labelPosition,
      startAngle,
      house.signName,
      shouldFlip,
      this.styles.signCircle.fontColor[signIndex],
      this.styles.signCircle.font
    )

    // 记录宫头星座标签位置用于悬浮检测
    this.signLabelPositions.push({
      type: 'signLabel',
      center: labelPosition,
      radius: 20, // 敏感区域半径
      house: house,
      signIndex: signIndex,
      signName: house.signName,
      remark: house.signRemark,
      angle: startAngle
    })

    // 绘制星座角度（度分）
    // 根据角度位置调整度分标签位置
    let degStartAngle = startAngle
    let minEndAngle = startAngle
    if (startAngle <= 180 && startAngle >= 0) {
      degStartAngle = startAngle - 5
      minEndAngle = startAngle + 7
    } else {
      degStartAngle = startAngle + 5
      minEndAngle = startAngle - 7
    }
    this.renderer.drawHouseLabel(
      CanvasUtils.getPoint(degStartAngle, signLabelRadius, this.renderer.center, this.renderer.zeroAngle),
      degStartAngle,
      house.signOffDeg,
      shouldFlip,
      this.styles.signCircle.fontColor[signIndex],
      this.styles.signCircle.degFont
    )
    this.renderer.drawHouseLabel(
      CanvasUtils.getPoint(minEndAngle, signLabelRadius, this.renderer.center, this.renderer.zeroAngle),
      minEndAngle,
      house.signOffMin,
      shouldFlip,
      this.styles.signCircle.fontColor[signIndex],
      this.styles.signCircle.degFont
    )
  }

  /**
   * 绘制宫位数字背景扇形
   * @param {number} startAngle - 起始角度
   * @param {number} endAngle - 结束角度
   */
  drawHouseNumberSector (startAngle, endAngle, house) {
    this.renderer.drawSector(
      startAngle,
      endAngle,
      this.styles.phaseCircle.radiusRatio,
      this.styles.houseSector.innerRadiusRatio,
      this.styles.phaseCircle.evenFill,
      this.styles.phaseCircle.strokeStyle,
      this.styles.phaseCircle.lineWidth,
      true // 绘制直边
    )

    // 记录后天宫数字扇形位置用于悬浮检测
    this.houseSectorPositions.push({
      startAngle,
      endAngle,
      innerRadius: this.renderer.radius * this.styles.phaseCircle.radiusRatio,
      outerRadius: this.renderer.radius * this.styles.houseSector.innerRadiusRatio,
      house: house,
      angle: CanvasUtils.normalizeAngle((startAngle + endAngle) / 2)
    })
  }

  /**
   * 绘制宫位数字标签
   * @param {number} startAngle - 起始角度
   * @param {number} endAngle - 结束角度
   * @param {Object} house - 宫位数据
   */
  drawHouseNumberLabel (startAngle, endAngle, house) {
    let midAngle = (startAngle + endAngle) / 2
    if (startAngle > endAngle) {
      midAngle = midAngle + 180
    }
    midAngle = CanvasUtils.normalizeAngle(midAngle)
    const shouldFlipMid = CanvasUtils.isAngleInFirstOrSecondQuadrant(midAngle)

    const houseLabelRadius = this.renderer.radius * this.styles.houseSector.innerRadiusRatio -
      this.renderer.radius * (this.styles.houseSector.innerRadiusRatio - this.styles.phaseCircle.radiusRatio) / 2

    this.renderer.drawHouseLabel(
      CanvasUtils.getPoint(midAngle, houseLabelRadius, this.renderer.center, this.renderer.zeroAngle),
      midAngle,
      house.houseNumber.toString(),
      shouldFlipMid,
      this.styles.houseLabel.fillStyle,
      this.styles.houseLabel.font
    )
  }

  /**
   * 计算两个圆周角之间的短弧中点。
   * 用于把被劫夺星座标签放在左右两个已绘制星座标签的视觉正中间。
   * @param {number} leftAngle - 左侧相邻标签角度
   * @param {number} rightAngle - 右侧相邻标签角度
   * @returns {number} 两个标签短弧中点角度
   */
  calcCircularMidAngle (leftAngle, rightAngle) {
    const normalizedLeft = CanvasUtils.normalizeAngle(leftAngle)
    const normalizedRight = CanvasUtils.normalizeAngle(rightAngle)
    let diff = normalizedRight - normalizedLeft
    if (diff > 180) diff -= 360
    if (diff < -180) diff += 360
    return CanvasUtils.normalizeAngle(normalizedLeft + diff / 2)
  }

  /**
   * 查找被劫夺星座左右两侧最近的已绘制星座标签角度。
   * @param {number} signIndex - 被劫夺星座索引
   * @param {Array} signLabelFlag - 星座标签标记数组
   * @param {Array} signLabelAngles - 已绘制星座标签角度数组
   * @returns {{leftAngle: number|null, rightAngle: number|null}}
   */
  findNeighborSignLabelAngles (signIndex, signLabelFlag, signLabelAngles) {
    let leftAngle = null
    let rightAngle = null

    for (let offset = 1; offset < 12; offset++) {
      const leftIndex = (signIndex - offset + 12) % 12
      if (signLabelFlag[leftIndex] === 1 && signLabelAngles[leftIndex] !== null && signLabelAngles[leftIndex] !== undefined) {
        leftAngle = signLabelAngles[leftIndex]
        break
      }
    }

    for (let offset = 1; offset < 12; offset++) {
      const rightIndex = (signIndex + offset) % 12
      if (signLabelFlag[rightIndex] === 1 && signLabelAngles[rightIndex] !== null && signLabelAngles[rightIndex] !== undefined) {
        rightAngle = signLabelAngles[rightIndex]
        break
      }
    }

    return { leftAngle, rightAngle }
  }

  /**
   * 判断调用方传入的是完整宫头标签实例，还是历史按星座索引保存的角度数组。
   * @param {Array} renderedSignLabels - 已绘制的宫头星座标签实例
   * @returns {boolean}
   */
  hasRenderedSignLabelEntries (renderedSignLabels) {
    return Array.isArray(renderedSignLabels) && renderedSignLabels.some(entry => (
      entry &&
      typeof entry === 'object' &&
      !Array.isArray(entry) &&
      Object.prototype.hasOwnProperty.call(entry, 'signIndex') &&
      Object.prototype.hasOwnProperty.call(entry, 'angle')
    ))
  }

  /**
   * 规范化宫头标签实例，供后续按圆周绘制顺序扫描。
   * @param {Array} renderedSignLabels - 已绘制的宫头星座标签实例
   * @returns {Array}
   */
  normalizeRenderedSignLabelEntries (renderedSignLabels) {
    if (!Array.isArray(renderedSignLabels)) return []

    return renderedSignLabels.reduce((entries, entry, order) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return entries

      const rawSignIndex = Number(entry.signIndex)
      const rawAngle = Number(entry.angle)
      if (!Number.isFinite(rawSignIndex) || !Number.isFinite(rawAngle)) return entries

      entries.push({
        ...entry,
        signIndex: CanvasUtils.normalizeAngle(Math.trunc(rawSignIndex)) % 12,
        angle: CanvasUtils.normalizeAngle(rawAngle),
        order
      })
      return entries
    }, [])
  }

  /**
   * 根据全部宫头标签实例计算劫夺标签位置。
   * @param {Array} signLabelFlag - 星座标签标记数组
   * @param {Array} renderedSignLabels - 已绘制的宫头星座标签实例
   * @returns {Array}
   */
  calcInterceptedPlacementsFromRenderedEntries (signLabelFlag, renderedSignLabels) {
    const originalSignLabelFlag = Array.isArray(signLabelFlag) ? signLabelFlag.slice() : []
    const renderedEntries = this.normalizeRenderedSignLabelEntries(renderedSignLabels)
      .sort((left, right) => left.angle - right.angle || left.order - right.order)

    if (renderedEntries.length < 2) return []

    const placements = []
    const placedSignIndexes = new Set()

    for (let index = 0; index < renderedEntries.length; index++) {
      const left = renderedEntries[index]
      const right = renderedEntries[(index + 1) % renderedEntries.length]
      const signStep = (right.signIndex - left.signIndex + 12) % 12

      // 按画布角度顺序扫描相邻标签，用星座步长判断中间是否跳过星座。
      if (signStep <= 1) continue

      const rightAngleInGap = index === renderedEntries.length - 1 ? right.angle + 360 : right.angle
      let angleGap = rightAngleInGap - left.angle
      if (angleGap < 0) angleGap += 360

      for (let offset = 1; offset < signStep; offset++) {
        const signIndex = (left.signIndex + offset) % 12
        if (originalSignLabelFlag[signIndex] !== 0 || placedSignIndexes.has(signIndex)) continue

        placements.push({
          signIndex,
          angle: CanvasUtils.normalizeAngle(left.angle + angleGap * offset / signStep)
        })
        placedSignIndexes.add(signIndex)
      }
    }

    return placements
  }

  /**
   * 历史调用兼容：优先使用按星座索引保存的角度，否则回退到理论星座中点。
   * @param {Array} signLabelFlag - 星座标签标记数组
   * @param {Object} pointsReal - 特殊点数据
   * @param {Array} houses - 宫位数组
   * @param {Array} signLabelAngles - 历史按星座索引保存的角度数组
   * @returns {Array}
   */
  calcFallbackInterceptedPlacements (signLabelFlag, pointsReal, houses, signLabelAngles = []) {
    const placements = []
    const signCount = this.signInfo.signName.length
    const originalSignLabelFlag = Array.isArray(signLabelFlag) ? signLabelFlag.slice() : []

    for (let i = 0; i < signCount; i++) {
      if (originalSignLabelFlag[i] !== 0) continue

      const { leftAngle, rightAngle } = this.findNeighborSignLabelAngles(i, originalSignLabelFlag, signLabelAngles)

      // 历史口径：优先取已知左右标签中点；无角度时退回理论先天宫中点换算。
      let midAngleDraw
      if (leftAngle !== null && rightAngle !== null) {
        midAngleDraw = this.calcCircularMidAngle(leftAngle, rightAngle)
      } else {
        const asc = pointsReal && Number.isFinite(Number(pointsReal.ASC)) ? Number(pointsReal.ASC) : 0
        const startAngleSign = CanvasUtils.normalizeAngle((i * 30 + 360 - asc) % 360)
        const midAngleReal = CanvasUtils.normalizeAngle(startAngleSign + 15)
        midAngleDraw = this.calcEqualHouseAngleByPosition(midAngleReal)
      }

      placements.push({ signIndex: i, angle: midAngleDraw })
    }

    return placements
  }

  /**
   * 绘制单个劫夺标签，并保留 tooltip 所需元数据。
   * @param {number} signIndex - 星座索引
   * @param {number} midAngleDraw - 标签绘制角度
   */
  drawInterceptedLabel (signIndex, midAngleDraw) {
    if (!Number.isFinite(midAngleDraw)) return

    const shouldFlipSign = CanvasUtils.isAngleInFirstOrSecondQuadrant(midAngleDraw)
    const signLabelRadius = this.renderer.radius * (this.styles.signCircle.outerRadiusRatio + this.styles.signCircle.innerRadiusRatio) / 2
    const labelPositionSign = CanvasUtils.getPoint(
      midAngleDraw,
      signLabelRadius,
      this.renderer.center,
      this.renderer.zeroAngle
    )

    this.renderer.drawHouseLabel(
      labelPositionSign,
      midAngleDraw,
      this.signInfo.signName[signIndex], // 本应该的名字
      shouldFlipSign,
      this.styles.signCircle.fontColor[signIndex],
      this.styles.signCircle.font
    )

    this.signLabelPositions.push({
      type: 'signLabel',
      center: labelPositionSign,
      radius: 20,
      signIndex: signIndex,
      signName: this.signInfo.signName[signIndex],
      angle: midAngleDraw,
      intercepted: true
    })
  }

  /**
   * 绘制劫夺标签。
   * @param {Array} signLabelFlag - 星座标签标记数组
   * @param {Object} pointsReal - 特殊点数据
   * @param {Array} houses - 宫位数组
   * @param {Array} renderedSignLabels - 宫头标签实例；历史调用也可传按星座索引保存的角度数组
   */
  drawInterceptedLabels (signLabelFlag, pointsReal, houses, renderedSignLabels = []) {
    const useRenderedEntries = this.hasRenderedSignLabelEntries(renderedSignLabels)
    const placements = useRenderedEntries
      ? this.calcInterceptedPlacementsFromRenderedEntries(signLabelFlag, renderedSignLabels)
      : this.calcFallbackInterceptedPlacements(signLabelFlag, pointsReal, houses, renderedSignLabels)

    // 先算完全部位置再绘制，避免边画边改 flag 影响后续环形间隙判断。
    placements.forEach(({ signIndex, angle }) => {
      if (!Number.isFinite(angle)) return

      this.drawInterceptedLabel(signIndex, angle)
      if (Array.isArray(signLabelFlag)) {
        signLabelFlag[signIndex] = 1
      }
    })
  }

  /**
   * 地平盘相位圈半径比例（含地平盘增量）。
   * 该值同时是地平盘 ASC/MC 轴线内端的落点，两处必须同源，禁止各写一套。
   * @returns {number} 地平盘相位圈半径比例
   */
  getHorizonPhaseCircleRadiusRatio () {
    // 0.41 + 0.05 在浮点下会得到 0.45999999999999996，这里收敛到 4 位小数，
    // 避免端点比相位圈圆线少一丝，也避免测试断言出现无意义的浮点差异。
    return Number((this.styles.phaseCircle.radiusRatio + HORIZON_PHASE_CIRCLE_DELTA).toFixed(4))
  }

  /**
   * 绘制以phaseCircle.radiusRatio为半径的圆圈
   * @param {boolean} isHorizon - 是否为地平盘模式
   *
   * 【说明】
   * 地平盘模式的方位角数据已通过 convertAzimuthToDrawAngle() 转换为逆时针绘制角度
   */
  drawPhaseCircle (isHorizon = false) {
    const ctx = this.renderer.ctx
    const center = this.renderer.center
    // 地平盘模式下增加 HORIZON_PHASE_CIRCLE_DELTA 的半径比例（地平盘轴线内端与此同源）
    const radiusRatio = isHorizon ? this.getHorizonPhaseCircleRadiusRatio() : this.styles.phaseCircle.radiusRatio
    const radius = this.renderer.radius * radiusRatio

    ctx.beginPath()
    ctx.arc(center.x, center.y, radius, 0, Math.PI * 2)
    ctx.strokeStyle = '#000'
    ctx.lineWidth = this.styles.phaseCircle.lineWidth
    ctx.stroke()
  }

  /**
   * 绘制相位连线
   * @param {Array} phases - 相位数据数组
   * @param {Array} planets - 行星数据数组
   * @param {boolean} [isFixRing=false] - 是否为固定环模式，true使用horizontalAzimuth，false使用position
   * @param {boolean} [isHorizon=false] - 是否为地平盘模式
   * @returns {Array} 相位位置信息
   *
   * 【地平盘绘制方向转换说明】
   * 后端返回的 horizontalAzimuth 是顺时针方位角（0度在正下方）
   * 需要转换为逆时针绘制角度：绘制角度 = 270 - 方位角
   */
  drawPhaseLines (phases, planets, isFixRing = false, isHorizon = false) {
    this.aspectPositions = []
    if (!phases.length) return

    const ctx = this.renderer.ctx
    const center = this.renderer.center
    // 地平盘模式下增加0.05的半径比例，减去1个像素
    const radiusRatio = isHorizon ? this.styles.phaseCircle.radiusRatio + 0.05 : this.styles.phaseCircle.radiusRatio
    const radius = this.renderer.radius * radiusRatio - 1

    // 创建行星位置映射表
    const planetIndexMap = {}
    planets.forEach(planet => {
      // 根据isFixRing参数选择使用哪个角度信息
      let angle
      if (isFixRing) {
        // 固定环模式：将方位角（顺时针）转换为逆时针绘制角度
        angle = this.convertAzimuthToDrawAngle(planet.horizontalAzimuth)
      } else {
        angle = planet.drawPos
      }
      planetIndexMap[planet.index] = CanvasUtils.normalizeAngle(angle)
    })

    phases.forEach(phase => {
      // 获取两个行星的位置
      const angle1 = planetIndexMap[phase.planet1Index]
      const angle2 = planetIndexMap[phase.planet2Index]

      if (angle1 === undefined || angle2 === undefined) return

      // 合相（ruleIndex === 0）不绘制，但记录到 aspectPositions 用于悬浮显示
      const isConjunction = phase.ruleIndex === 0

      // 根据规则索引获取颜色
      const ruleIndex = Math.min(phase.ruleIndex, this.styles.phaseSymbol.colors.length - 1)
      const color = this.styles.phaseSymbol.colors[ruleIndex]

      // 修改2：使用phases中的ruleSymbol字段
      const symbol = phase.ruleSymbol || this.getDefaultSymbol(ruleIndex)

      // 计算两个点在圆环上的位置
      const point1 = CanvasUtils.getPoint(angle1, radius, center, this.renderer.zeroAngle)
      const point2 = CanvasUtils.getPoint(angle2, radius, center, this.renderer.zeroAngle)

      // 计算符号位置
      const symbolPos = this.calculateSymbolPosition(point1, point2)

      // 记录相位位置信息（所有相位都记录，用于悬浮检测）
      this.aspectPositions.push({
        phase,
        planet1Index: phase.planet1Index,
        planet2Index: phase.planet2Index,
        center: symbolPos,
        radius: 15, // 悬停敏感区域半径
        angle: CanvasUtils.normalizeAngle((angle1 + angle2) / 2) // 新增：用于排序的角度
      })

      // 合相不绘制相位线和符号
      if (isConjunction) {
        return
      }

      // 设置连线样式
      ctx.strokeStyle = color
      ctx.lineWidth = this.styles.phaseLine.lineWidth

      // 绘制断开的连线（符号位置不画线）
      this.drawBrokenLine(point1, point2, symbolPos, this.styles.phaseSymbol.gapSize)

      // 绘制相位符号（在连线中点）
      this.drawPhaseSymbol(symbolPos, symbol, color, ruleIndex)
    })
  }

  /**
   * 绘制行星
   * @param {Array} planets - 行星数据数组
   * @returns {Array} 行星位置信息
   */
  drawPlanets (planets, refreshCache = true) {
    if (refreshCache) {
      this.planetPositionsLookup = [] // 清空上一帧的位置记录
    }

    planets.forEach(planet => {
      const pos = planet.drawPos
      if (!planet || pos === undefined || pos === null) {
        return
      }

      const angle = CanvasUtils.normalizeAngle(pos)

      // 绘制行星符号（径向文字）
      const displayName = planet.shortName || planet.name || ''
      const displayColor = planet.color || '#FFFFFF'
      const symbolPos = this.renderer.drawRadialText(
        angle,
        this.styles.planet.textRadiusRatio,
        displayName,
        displayColor,
        this.styles.planet.planetNameFont
      )

      // 记录行星/逻辑点位置和基本信息
      this.planetPositionsLookup.push({
        type: 'planet',
        data: planet, // 统一用 data 字段存储，便于悬停检测
        center: symbolPos,
        angle: angle, // 添加绘制角度用于排序
        radius: 20 // 悬停敏感区域半径
      })

      // 绘制行星位置点
      this.renderer.drawLabeledCircle(
        angle,
        this.styles.planet.planetRadiusRatio,
        displayColor,
        this.styles.planetDot.radius
      )
      // 绘制疾行/缓慢
      this.renderer.drawRadialText(
        angle,
        this.styles.planet.speedRadiusRatio,
        planet.speed || '',
        planet.color,
        this.styles.planet.degFont
      )
      // 绘制行星连线
      // this.renderer.drawPlanetConnection(
      //   symbolPos,
      //   dotPos,
      //   planet.color,
      //   this.styles.planet.lineWidth
      // )
      // 绘制星座内偏移角度文本
      this.renderer.drawRadialText(
        angle,
        this.styles.planet.degTextRadiusRatio,
        planet.signOffDeg + '°',
        planet.color,
        this.styles.planet.degFont
      )
      // 绘制星座名
      this.renderer.drawRadialText(
        angle,
        this.styles.planet.signNameTextRadiusRatio,
        planet.signName,
        planet.color,
        this.styles.planet.signFont
      )
      // 绘制星座内偏移分文本
      this.renderer.drawRadialText(
        angle,
        this.styles.planet.minTextRadiusRatio,
        planet.signOffMin + '′',
        planet.color,
        this.styles.planet.degFont
      )
      // 绘制逆行
      this.renderer.drawRadialText(
        angle,
        this.styles.planet.directionRadiusRatio,
        planet.direction || '',
        planet.color,
        this.styles.planet.speedFont
      )
    })
  }

  /**
   * 绘制催贵公式中点
   * @param {Array} midpoints - 中点数据数组
   * @param {Array} planets - 行星数据数组（用于查找行星名称）
   */
  drawMidpoints (midpoints, planets) {
    if (!midpoints || midpoints.length === 0) return

    // 中点使用的样式（与行星类似但使用不同颜色）
    const midpointColor = '#9b59b6' // 紫色表示中点

    midpoints.forEach(midpoint => {
      const pos = midpoint.drawPos
      if (pos === undefined || pos === null) {
        return
      }

      const angle = CanvasUtils.normalizeAngle(pos)

      // 生成中点显示名称（如 "日·月"），用"·"替代"-"
      const name = midpoint.name || midpoint.shortName || ''
      const displayName = name.replace(/-/g, '·')

      // 绘制中点符号（沿半径方向，字间距最小）
      const symbolPos = this.renderer.drawTextAlongRadius(
        angle,
        this.styles.planet.textRadiusRatio + 0.02,
        10,
        displayName,
        { fillStyle: midpointColor, font: this.styles.planet.midpointNameFont }
      )

      // 记录中点位置和基本信息（用于悬停检测）
      this.planetPositionsLookup.push({
        type: 'midpoint',
        data: midpoint,
        center: symbolPos,
        angle: angle, // 添加绘制角度用于排序
        radius: 20
      })

      // 绘制中点位置点（使用不同样式区分）
      this.renderer.drawLabeledCircle(
        angle,
        this.styles.planet.planetRadiusRatio,
        midpointColor,
        this.styles.planetDot.radius
      )

      // 绘制中点角度文本（后端已带符号）
      if (midpoint.signOffDeg !== undefined && midpoint.signOffDeg !== null) {
        this.renderer.drawRadialText(
          angle,
          this.styles.planet.degTextRadiusRatio - 0.04,
          midpoint.signOffDeg,
          midpointColor,
          this.styles.planet.degFont
        )
      }

      // 绘制中点分文本（后端已带符号）
      if (midpoint.signOffMin !== undefined && midpoint.signOffMin !== null) {
        this.renderer.drawRadialText(
          angle,
          this.styles.planet.minTextRadiusRatio - 0.04,
          midpoint.signOffMin,
          midpointColor,
          this.styles.planet.degFont
        )
      }

      // 绘制先天宫名称
      if (midpoint.signName) {
        this.renderer.drawRadialText(
          angle,
          this.styles.planet.signNameTextRadiusRatio - 0.04,
          midpoint.signName,
          midpointColor,
          this.styles.planet.signFont
        )
      }
    })
  }

  /**
   * 绘制恒星
   * @param {Array} stars - 恒星数据数组
   */
  drawStars (stars) {
    if (!this.refreshAlarmStar) {
      this.starPositions = [] // 只有全量绘制时才清空并重新记录位置
    }

    stars.forEach(star => {
      const angle = CanvasUtils.normalizeAngle(star.drawPos)
      if (angle === undefined || angle === null) {
        return
      }

      let starColor
      let fontColor

      // 绘制恒星圆点（金色）
      // 用*号代替圆点
      if (star.alarm) {
        if (this.flinkStatus) {
          starColor = star.alarmColor
          fontColor = star.alarmColor
        } else {
          starColor = '#fff'
          fontColor = '#fff'
        }
      } else {
        starColor = this.styles.star.dotColor
        fontColor = this.styles.star.fontColor
      }
      // 如果只刷新报警星，并且没有报警，则不绘制*号
      if (this.refreshAlarmStar && !star.alarm) {
        return
      }
      this.renderer.drawAsterisk(
        angle,
        this.styles.star.starRadiusRatio, // 恒星*号半径比例
        '*',
        starColor, // 金色
        this.styles.star.dotFont // *号半径
      )
      // 使用新方法绘制恒星名称（沿半径方向）
      this.renderer.drawTextAlongRadius(
        angle,
        this.styles.star.textRadiusRatio, // 起始位置比例
        this.styles.star.textSpacing, // 字符间距（像素）
        star.starName,
        {
          fillStyle: fontColor,
          font: this.styles.star.starNameFont
        }
      )

      if (!this.refreshAlarmStar) {
        // 计算第一个字符的位置（用于悬浮检测）
        const firstCharDistance = this.renderer.radius * this.styles.star.textRadiusRatio
        const radian = CanvasUtils.degToRad(angle)
        const textCenter = {
          x: this.renderer.center.x + Math.cos(radian) * firstCharDistance,
          y: this.renderer.center.y + Math.sin(radian) * firstCharDistance
        }

        // 记录恒星位置和基本信息（用于悬浮判断）
        this.starPositions.push({
          type: 'star',
          star,
          center: textCenter,
          textCenter: textCenter,
          angle: angle, // 添加绘制角度用于排序
          radius: 20 // 悬浮敏感区域半径
        })
      }
    })
  }

  /**
   * 查找恒星位置（用于悬浮判断）
   * @param {number} x - 鼠标X坐标
   * @param {number} y - 鼠标Y坐标
   * @returns {Object|null} 恒星数据
   */
  findStarAtPosition (x, y) {
    const matchedStars = []
    for (const starPos of this.starPositions) {
      const dx = x - starPos.textCenter.x
      const dy = y - starPos.textCenter.y
      const distanceSq = dx * dx + dy * dy // 使用距离平方优化性能
      if (distanceSq <= starPos.radius * starPos.radius) {
        matchedStars.push(starPos) // 返回完整的starPos对象包含angle
      }
    }
    return matchedStars
  }

  /**
   * 地平盘固定环：按水平方位角绘制 ASC/MC 短轴线。
   * 配色与黄道盘/本命盘保持同一约定：ASC 红(hcolor)、MC 蓝(vcolor)；
   * 轴线落位仍严格使用后端返回的方位角，不做任何对调。
   *
   * 【端点对齐】地平盘画面上可见的圆圈描边只有相位圈与外环（24 山向环）内外沿，
   * 因此地平盘轴线两端必须与这两条圆线同源，不能沿用黄道盘宫位圈的 0.45 ~ 0.76：
   * - 外端 = outerRing.innerRadiusRatio（24 山向环内沿那条圆线）
   * - 内端 = getHorizonPhaseCircleRadiusRatio()（地平盘相位圈那条圆线）
   * 否则外端会悬空在圆线内侧、内端会越过圆线戳进里侧区域。
   * @param {Object} horizontalSpecialPoints - 水平特殊点方位角
   */
  drawHorizonSpecialAxes (horizontalSpecialPoints = {}) {
    const axes = [
      { type: 'ASC', color: this.styles.axisArrow.hcolor },
      { type: 'MC', color: this.styles.axisArrow.vcolor }
    ]
    const outerRadiusRatio = this.styles.outerRing.innerRadiusRatio
    const innerRadiusRatio = this.getHorizonPhaseCircleRadiusRatio()

    axes.forEach(({ type, color }) => {
      const azimuth = horizontalSpecialPoints[type]
      if (!Number.isFinite(azimuth)) return

      this.renderer.drawAxisArrow(
        this.convertAzimuthToDrawAngle(azimuth),
        color,
        this.styles.axisArrow.lineWidth,
        outerRadiusRatio,
        innerRadiusRatio,
        false
      )
    })
  }

  /**
   * 绘制特殊轴线
   * @param {Object} specialPoints - 特殊点数据
   */
  drawSpecialAxis (specialPoints) {
    const { ASC: ascEqualAngle, MC: mcEqualAngle } = this.getSpecialAxisDisplayAngles(specialPoints)

    // 7. 绘制特殊轴线
    if (specialPoints.ASC !== undefined) {
      // 绘制ASC-DESC轴线
      const ascAxisPos = this.renderer.drawAxisArrow(
        ascEqualAngle,
        this.styles.axisArrow.hcolor,
        this.styles.axisArrow.lineWidth,
        this.styles.axisArrow.outerRadiusRatio,
        this.styles.axisArrow.innerRadiusRatio
      )

      // 记录ASC位置（用于悬浮检测，矩形区域检测）
      // 轴线位置使用矩形检测
      this.specialAxisPositions.push(
        { type: 'ASC', center: ascAxisPos.center, angle: ascEqualAngle, rectWidth: 120, rectLength: 12 }
      )

      // DES 标签保持禁画：当前需求仅保留 ASC 方向，不显示对宫文字。
    }

    if (specialPoints.MC !== undefined) {
      // 绘制MC-IC轴线（使用等分角度）
      const mcAxisPos = this.renderer.drawAxisArrow(
        mcEqualAngle,
        this.styles.axisArrow.vcolor,
        this.styles.axisArrow.lineWidth,
        this.styles.axisArrow.outerRadiusRatio,
        this.styles.axisArrow.innerRadiusRatio
      )

      // 记录MC位置（用于悬浮检测，矩形区域检测）
      this.specialAxisPositions.push(
        { type: 'MC', center: mcAxisPos.center, angle: mcEqualAngle, rectWidth: 120, rectLength: 12 }
      )

      // IC 标签保持禁画：当前需求仅保留 MC 方向，不显示对宫文字。
    }
  }

  /**
   * 计算符号位置
   * @param {Object} point1 - 第一个点坐标 {x, y}
   * @param {Object} point2 - 第二个点坐标 {x, y}
   * @returns {Object} 符号位置 {x, y}
   */
  calculateSymbolPosition (point1, point2) {
    const midX = (point1.x + point2.x) / 2
    const midY = (point1.y + point2.y) / 2

    // 计算偏移方向（垂直于连线方向）
    const lineVecX = point2.x - point1.x
    const lineVecY = point2.y - point1.y

    // 计算垂直向量（逆时针旋转90度）
    let perpVecX = -lineVecY
    let perpVecY = lineVecX

    // 归一化垂直向量
    const perpDist = Math.sqrt(perpVecX * perpVecX + perpVecY * perpVecY)
    if (perpDist > 0) {
      perpVecX /= perpDist
      perpVecY /= perpDist
    }

    // 计算符号位置（沿垂直方向偏移）
    const offset = this.styles.phaseSymbol.offset
    return {
      x: midX + perpVecX * offset,
      y: midY + perpVecY * offset
    }
  }

  /**
   * 绘制断开的连线（符号位置不画线）
   * @param {Object} point1 - 第一个点坐标 {x, y}
   * @param {Object} point2 - 第二个点坐标 {x, y}
   * @param {Object} symbolPos - 符号位置 {x, y}
   * @param {number} gapSize - 间隙大小
   */
  drawBrokenLine (point1, point2, symbolPos, gapSize) {
    // 计算连线方向向量
    const dirX = point2.x - point1.x
    const dirY = point2.y - point1.y
    const length = Math.sqrt(dirX * dirX + dirY * dirY)

    // 计算单位方向向量
    const unitDirX = dirX / length
    const unitDirY = dirY / length

    // 计算符号在连线上的投影位置
    const toSymbolX = symbolPos.x - point1.x
    const toSymbolY = symbolPos.y - point1.y
    const dotProduct = toSymbolX * unitDirX + toSymbolY * unitDirY

    // 计算间隙位置
    const gapStart = Math.max(0, dotProduct - gapSize / 2)
    const gapEnd = Math.min(length, dotProduct + gapSize / 2)

    // 绘制前段连线（point1到间隙起点）
    if (gapStart > 0) {
      const startX = point1.x + unitDirX * gapStart
      const startY = point1.y + unitDirY * gapStart
      this.ctx.beginPath()
      this.ctx.moveTo(point1.x, point1.y)
      this.ctx.lineTo(startX, startY)
      this.ctx.stroke()
    }

    // 绘制后段连线（间隙终点到point2）
    if (gapEnd < length) {
      const endX = point1.x + unitDirX * gapEnd
      const endY = point1.y + unitDirY * gapEnd
      this.ctx.beginPath()
      this.ctx.moveTo(endX, endY)
      this.ctx.lineTo(point2.x, point2.y)
      this.ctx.stroke()
    }
  }

  /**
   * 在指定位置绘制相位符号
   * @param {Object} pos - 符号位置 {x, y}
   * @param {string} symbol - 相位符号
   * @param {string} color - 符号颜色
   * @param {number} ruleIndex - 相位规则索引
   */
  drawPhaseSymbol (pos, symbol, color, ruleIndex) {
    const ctx = this.renderer.ctx

    // 设置文本样式
    ctx.fillStyle = color
    ctx.font = this.styles.phaseSymbol.font
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // // 特殊符号处理
    // if (['□', '△'].includes(symbol)) {
    //   ctx.font = 'bold 20px Arial'
    // }
    ctx.fillText(symbol, pos.x, pos.y)
  }

  /**
   * 绘制刻度圈
   * @param {boolean} showFixRing - 是否显示固定环
   * @param {Object} pointsReal - 特殊点数据
   */
  drawScaleCircle (showFixRing, pointsReal) {
    if (!this.scaleStyles.show) return

    const ctx = this.renderer.ctx
    const center = this.renderer.center
    const baseRadius = this.renderer.radius
    const outerRadius = baseRadius * this.scaleStyles.outerRadiusRatio

    ctx.save()

    // 设置样式
    ctx.strokeStyle = this.scaleStyles.tickColor
    ctx.fillStyle = this.scaleStyles.textColor
    ctx.font = this.scaleStyles.textFont
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.lineWidth = 1

    // 获取ASC角度（如果存在）
    const ascAngle = pointsReal && pointsReal.ASC !== undefined ? pointsReal.ASC : 0

    // 计算起始角度
    let startAngle = 0
    if (!showFixRing) {
      // 当showFixRing为假时，0刻度线从-ASC位置开始
      startAngle = -ascAngle
    }

    // 绘制刻度线
    let angle = 0
    for (let drawAngle = CanvasUtils.normalizeAngle(startAngle); drawAngle > CanvasUtils.normalizeAngle(startAngle) - 360; drawAngle -= 5) {
      const realDrawAngle = CanvasUtils.normalizeAngle(drawAngle)

      const isLargeTick = angle % 10 === 0
      const tickLength = isLargeTick ? this.scaleStyles.largeTickLength : this.scaleStyles.smallTickLength

      const startRadius = outerRadius
      const endRadius = outerRadius + tickLength

      const startPoint = CanvasUtils.getPoint(realDrawAngle, startRadius, center, this.renderer.zeroAngle)
      const endPoint = CanvasUtils.getPoint(realDrawAngle, endRadius, center, this.renderer.zeroAngle)

      // 绘制刻度线
      ctx.beginPath()
      ctx.moveTo(startPoint.x, startPoint.y)
      ctx.lineTo(endPoint.x, endPoint.y)
      ctx.stroke()

      // 绘制大刻度文字
      if (isLargeTick) {
        const textRadius = outerRadius + this.scaleStyles.textOffset
        const textPoint = CanvasUtils.getPoint(realDrawAngle, textRadius, center, this.renderer.zeroAngle)

        ctx.save()
        ctx.translate(textPoint.x, textPoint.y)
        ctx.fillText(angle.toString(), 0, 0)
        ctx.restore()
      }
      angle += 5
    }

    ctx.restore()
  }

  /**
   * 更新刻度样式
   * @param {Object} scaleStyles - 新的刻度样式
   */
  updateScaleStyles (scaleStyles) {
    this.scaleStyles = { ...this.scaleStyles, ...scaleStyles }
  }

  /**
   * 获取默认相位符号
   * @param {number} ruleIndex - 相位规则索引
   * @returns {string} 默认符号
   */
  getDefaultSymbol (ruleIndex) {
    // 默认符号映射表
    const defaultSymbols = {
      0: '☌', // 合相
      1: '⚺', // 半六合
      2: '∠', // 半刑
      3: '⚹', // 六分相
      4: 'Q', // 五分相
      5: '□', // 四分相
      6: '△', // 三分相
      7: '⚼', // 补八分相
      8: 'bQ', // 倍五分相
      9: '​​⚻​', // 补十二分相
      10: '☍' // 二分相
    }
    return defaultSymbols[ruleIndex] || '?'
  }

  /**
   * 根据行星ID查找行星
   * @param {Array} planets - 行星数据数组
   * @param {number} planetId - 行星ID
   * @returns {Object} 行星对象
   */
  findPlanetById (planets, planetId) {
    if (!planets) return { fullName: '未知行星' }
    return planets.find(p => p.index === planetId) || { fullName: '未知行星' }
  }

  // 添加MS定时器方法
  /**
   * 启动MS定时器
   * @param {number} interval - 定时器间隔(毫秒)
   * @param {Function} callback - 定时器回调函数
   * @returns {boolean} 是否成功启动
   */
  startMsTimer (interval, callback) {
    // 先停止现有定时器
    this.stopMsTimer()

    // 验证参数
    if (typeof interval !== 'number' || interval <= 0) {
      console.error('MS定时器间隔必须为正数')
      return false
    }

    if (typeof callback !== 'function') {
      console.error('MS定时器回调必须为函数')
      return false
    }

    // 设置定时器参数
    this.msTimerInterval = interval
    this.msTimerCallback = callback

    // 启动定时器
    this.msTimerRunning = true
    this.msTimer = setInterval(() => {
      if (this.msTimerRunning && this.msTimerCallback) {
        try {
          this.refreshAlarmStar = true
          this.msTimerCallback()
          this.toggleFlinkStatus()
        } catch (error) {
          console.error('MS定时器回调执行错误:', error)
        } finally {
          this.refreshAlarmStar = false
        }
      }
    }, this.msTimerInterval)

    console.log(`MS定时器已启动，间隔: ${interval}ms`)
    return true
  }

  /**
   * 停止MS定时器
   */
  stopMsTimer () {
    if (this.msTimer) {
      clearInterval(this.msTimer)
      this.msTimer = null
    }
    this.msTimerRunning = false
    this.msTimerCallback = null
    this.refreshAlarmStar = false
    this.flinkStatus = false
    console.log('MS定时器已停止')
  }

  /**
   * 检查MS定时器是否正在运行
   * @returns {boolean} 定时器运行状态
   */
  isMsTimerRunning () {
    return this.msTimerRunning
  }

  /**
   * 获取MS定时器当前间隔
   * @returns {number} 当前间隔(毫秒)
   */
  getMsTimerInterval () {
    return this.msTimerInterval
  }

  /**
   * 修改MS定时器间隔（立即生效）
   * @param {number} interval - 新的间隔(毫秒)
   * @returns {boolean} 是否成功修改
   */
  setMsTimerInterval (interval) {
    if (typeof interval !== 'number' || interval <= 0) {
      console.error('MS定时器间隔必须为正数')
      return false
    }

    // 如果定时器正在运行，需要重启
    const wasRunning = this.msTimerRunning
    if (wasRunning) {
      this.stopMsTimer()
    }

    this.msTimerInterval = interval

    if (wasRunning && this.msTimerCallback) {
      this.startMsTimer(interval, this.msTimerCallback)
    }

    return true
  }

  // 在destroy方法中添加定时器清理

  /**
   * 绘制最外圈圆环（24格扇形环）
   * 每个格子中填写this.fixWord中的字，按逆时针方向排列
   * "子"从zeroAngle开始（全局已设置）
   */
  drawOuterRing () {
    const ctx = this.renderer.ctx

    // 绘制24个扇形格子
    for (let i = 0; i < 24; i++) {
      // 计算每个扇形的角度（逆时针方向，从0度开始）
      // zeroAngle已在全局设置好，此处直接使用标准角度
      const startAngle = i * 15 - (15 / 2)
      const endAngle = (i + 1) * 15 - (15 / 2)

      // 使用renderer的drawSector方法绘制扇形
      this.renderer.drawSector(
        startAngle,
        endAngle,
        this.styles.outerRing.innerRadiusRatio,
        this.styles.outerRing.outerRadiusRatio,
        '#fff', // 全部使用白色填充
        this.styles.outerRing.strokeStyle,
        this.styles.outerRing.lineWidth,
        true // 绘制直边
      )

      // 计算文字位置（扇形中心）
      const midAngle = (startAngle + endAngle) / 2
      const textRadius = this.renderer.radius *
        (this.styles.outerRing.innerRadiusRatio + this.styles.outerRing.outerRadiusRatio) / 2

      const textPosition = CanvasUtils.getPoint(
        midAngle,
        textRadius,
        this.renderer.center,
        this.renderer.zeroAngle
      )

      // 绘制文字（不旋转）
      const wordIndex = (this.fixWord.length - i) % this.fixWord.length
      const fontColorArr = this.styles.outerRing.fontColor
      ctx.fillStyle = Array.isArray(fontColorArr) ? fontColorArr[wordIndex] : fontColorArr
      ctx.font = this.styles.outerRing.font
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      // 使用this.fixWord中的字，按顺时针顺序排列（数组中是顺时针，但系统绘制是逆时针，所以只能逆时针取）
      ctx.fillText(this.fixWord[wordIndex], textPosition.x, textPosition.y)
    }
  }

  destroy () {
    // 清理MS定时器
    this.stopMsTimer()
  }

  toggleFlinkStatus () {
    this.flinkStatus = !this.flinkStatus
  }

  /**
   * 设置特殊点数据（用于悬浮检测）
   * @param {Object} pointsReal - 特殊点数据
   */
  setPointsReal (pointsReal) {
    this.pointsReal = pointsReal || {}
  }

  /**
   * 绘制坐山箭头（地平盘使用）
   * @param {number} zuoshan - 坐山度数（顺时针，0度在正下方）
   */
  drawZuoshanArrow (zuoshan) {
    if (zuoshan === null || zuoshan === undefined) {
      // 如果坐山为空，不绘制箭头
      return
    }

    const ctx = this.renderer.ctx
    const center = this.renderer.center
    const radius = this.renderer.radius * 0.78 // 箭头绘制在outerRing外侧

    // 坐山的0度（正下方）对应zeroAngle度（对于地平盘，zeroAngle=270）
    // 坐山的顺时针角度需要转换成Canvas的逆时针角度
    const startAngle = CanvasUtils.normalizeAngle(360 - zuoshan) // 坐山起点角度（顺时针）
    const endAngle = CanvasUtils.normalizeAngle(360 - zuoshan + 180) // 指向对面

    // 使用zeroAngle将坐山的顺时针角度（0度在正下方）转换成Canvas的逆时针角度
    const startPoint = CanvasUtils.getPoint(startAngle, radius, center, this.renderer.zeroAngle)
    const endPoint = CanvasUtils.getPoint(endAngle, radius, center, this.renderer.zeroAngle)

    // 绘制箭头
    ctx.save()
    ctx.strokeStyle = '#006400' // 箭头颜色：深绿色
    ctx.fillStyle = '#006400'
    ctx.lineWidth = 1
    ctx.lineCap = 'round'

    // 绘制箭头主线
    ctx.beginPath()
    ctx.moveTo(startPoint.x, startPoint.y)
    ctx.lineTo(endPoint.x, endPoint.y)
    ctx.stroke()

    // 绘制箭头头部（在终点，实心）
    const arrowLength = -20
    const arrowWidth = 30 // 箭头两翼的角度差
    // 箭头两翼从圆心计算，使用zeroAngle
    const leftWing = CanvasUtils.getPoint(endAngle - arrowWidth, arrowLength, endPoint, this.renderer.zeroAngle)
    const rightWing = CanvasUtils.getPoint(endAngle + arrowWidth, arrowLength, endPoint, this.renderer.zeroAngle)

    ctx.beginPath()
    ctx.moveTo(endPoint.x, endPoint.y)
    ctx.lineTo(leftWing.x, leftWing.y)
    ctx.lineTo(rightWing.x, rightWing.y)
    ctx.closePath()
    ctx.fill() // 实心填充

    // 绘制"坐山"标签，使用zeroAngle
    const labelPoint = CanvasUtils.getPoint(startAngle - 5, radius - 25, center, this.renderer.zeroAngle)
    ctx.font = 'bold 14px Arial'
    ctx.fillStyle = '#006400' // 深绿色
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('坐山', labelPoint.x, labelPoint.y)

    ctx.restore()
  }

  /**
   * 固定环模式：绘制28星宿格子（使用方位角）
   * @param {Array} xingxiuPositions - 星宿位置数组
   *
   * 【地平盘绘制方向转换说明】
   * 后端返回的 horizontalAzimuth 是顺时针方位角（0度在正下方）
   * 需要转换为逆时针绘制角度：绘制角度 = 270 - 方位角
   */
  drawConstellationsSectorFixRing (xingxiuPositions) {
    this.constellationSectors = [] // 清空之前的位置记录

    // 绘制28星宿格子
    for (let i = 0; i < xingxiuPositions.length; i++) {
      // 将方位角（顺时针）转换为逆时针绘制角度
      const startAngle = this.convertAzimuthToDrawAngle(xingxiuPositions[i].horizontalAzimuth)
      const endAngle = this.convertAzimuthToDrawAngle(xingxiuPositions[(i + 1) % xingxiuPositions.length].horizontalAzimuth)

      // 记录扇环信息
      const sector = {
        index: i,
        startAngle,
        endAngle,
        innerRadius: this.renderer.radius * this.styles.constellations.innerRadiusRatio,
        outerRadius: this.renderer.radius * this.styles.constellations.outerRadiusRatio,
        // 不再记录 absoluteLongitude（固定环模式同理）：理由见 drawConstellations 中的说明
        xingxiuName: xingxiuPositions[i].xingxiuName,
        horizontalAltitude: xingxiuPositions[i].horizontalAltitude // 使用高度信息
      }
      this.constellationSectors.push(sector)

      const xingxiuColor = xingxiuPositions[i].xingxiuColor

      this.renderer.drawSector(
        startAngle,
        endAngle,
        this.styles.constellations.innerRadiusRatio,
        this.styles.constellations.outerRadiusRatio,
        xingxiuColor,
        this.styles.constellations.strokeStyle,
        this.styles.constellations.lineWidth,
        true // 是否绘制直边
      )
    }
  }

  /**
   * 固定环模式：绘制行星（使用方位角）
   * @param {Array} planets - 行星数据数组
   * @param {boolean} refreshChache - 是否刷新缓存
   *
   * 【地平盘绘制方向转换说明】
   * 后端返回的 horizontalAzimuth 是顺时针方位角（0度在正下方）
   * 需要转换为逆时针绘制角度：绘制角度 = 270 - 方位角
   */
  drawPlanetsFixRing (planets, refreshCache = true) {
    if (refreshCache) {
      this.planetPositionsLookup = [] // 清空上一帧的位置记录
    }

    planets.forEach(planet => {
      // 将方位角（顺时针）转换为逆时针绘制角度
      const angle = this.convertAzimuthToDrawAngle(planet.horizontalAzimuth)

      // 绘制行星符号（径向文字）
      const displayName = planet.shortName || planet.name || ''
      const displayColor = planet.color || '#FFFFFF'
      const symbolPos = this.renderer.drawRadialText(
        angle,
        this.styles.planet.textRadiusRatio,
        displayName,
        displayColor,
        this.styles.planet.planetNameFont
      )

      // 记录行星/逻辑点位置和基本信息
      this.planetPositionsLookup.push({
        type: 'planet',
        data: planet,
        center: symbolPos,
        angle: angle, // 确保记录了绘制角度
        radius: 20 // 悬停敏感区域半径
      })

      // 绘制行星位置点
      this.renderer.drawLabeledCircle(
        angle,
        this.styles.planet.planetRadiusRatio,
        displayColor,
        this.styles.planetDot.radius
      )

      // 绘制方位角信息（仅地平盘显示）
      if (planet.horizontalAzimuth !== undefined && planet.horizontalAzimuth !== null) {
        this.renderer.drawRadialText(
          angle,
          this.styles.planet.azimuthRadiusRatio || 0.58,
          planet.horizontalAzimuth.toFixed(1) + '°', // 显示方位角
          planet.color,
          this.styles.planet.degFont
        )
      }

      // 绘制速度信息
      this.renderer.drawRadialText(
        angle,
        this.styles.planet.speedRadiusRatio,
        planet.speed || '',
        planet.color,
        this.styles.planet.speedFont
      )

      // 绘制逆行信息
      this.renderer.drawRadialText(
        angle,
        this.styles.planet.directionRadiusRatio,
        planet.direction || '',
        planet.color,
        this.styles.planet.speedFont
      )
    })
  }

  /**
   * 固定环模式：绘制催贵公式中点（使用方位角）
   * @param {Array} midpoints - 中点数据数组
   * @param {Array} planets - 行星数据数组
   *
   * 【地平盘绘制方向转换说明】
   * 后端返回的 horizontalAzimuth 是顺时针方位角（0度在正下方）
   * 需要转换为逆时针绘制角度：绘制角度 = 270 - 方位角
   */
  drawMidpointsFixRing (midpoints, planets) {
    if (!midpoints || midpoints.length === 0) return

    // 地平盘中点只使用方位角定位，高度角不参与中点计算或绘制。
    const midpointColor = '#9b59b6' // 紫色表示中点

    midpoints.forEach(midpoint => {
      // 将方位角（顺时针）转换为逆时针绘制角度
      const angle = this.convertAzimuthToDrawAngle(midpoint.horizontalAzimuth)

      // 生成中点显示名称（如 "日·月"），用"·"替代"-"
      const name = midpoint.name || midpoint.shortName || ''
      const displayName = name.replace(/-/g, '·')

      // 绘制中点符号（沿半径方向，字间距最小）
      const symbolPos = this.renderer.drawTextAlongRadius(
        angle,
        this.styles.planet.textRadiusRatio,
        0,
        displayName,
        { fillStyle: midpointColor, font: this.styles.planet.midpointNameFont }
      )

      // 记录中点位置
      this.planetPositionsLookup.push({
        type: 'midpoint',
        data: midpoint,
        center: symbolPos,
        angle: angle, // 确保记录了绘制角度
        radius: 20
      })

      // 绘制中点位置点
      this.renderer.drawLabeledCircle(
        angle,
        this.styles.planet.planetRadiusRatio,
        midpointColor,
        this.styles.planetDot.radius
      )

      // 绘制先天宫名称（如有）
      if (midpoint.signName) {
        this.renderer.drawRadialText(
          angle,
          this.styles.planet.signNameTextRadiusRatio,
          midpoint.signName,
          midpointColor,
          this.styles.planet.signFont
        )
      }

      // 绘制中点分文本（如有，后端已带符号）
      if (midpoint.signOffMin !== undefined && midpoint.signOffMin !== null) {
        this.renderer.drawRadialText(
          angle,
          this.styles.planet.minTextRadiusRatio,
          midpoint.signOffMin,
          midpointColor,
          this.styles.planet.degFont
        )
      }
    })
  }

  /**
   * 固定环模式：绘制恒星（使用方位角）
   * @param {Array} stars - 恒星数据数组
   *
   * 【地平盘绘制方向转换说明】
   * 后端返回的 horizontalAzimuth 是顺时针方位角（0度在正下方）
   * 需要转换为逆时针绘制角度：绘制角度 = 270 - 方位角
   */
  drawStarsFixRing (stars) {
    if (!this.refreshAlarmStar) {
      this.starPositions = [] // 只有全量绘制时才清空并重新记录位置
    }

    stars.forEach(star => {
      // 将方位角（顺时针）转换为逆时针绘制角度
      const angle = this.convertAzimuthToDrawAngle(star.horizontalAzimuth)

      let starColor
      let fontColor

      // 绘制恒星圆点（金色）
      // 用*号代替圆点
      if (star.alarm) {
        if (this.flinkStatus) {
          starColor = star.alarmColor
          fontColor = star.alarmColor
        } else {
          starColor = '#fff'
          fontColor = '#fff'
        }
      } else {
        starColor = this.styles.star.dotColor
        fontColor = this.styles.star.fontColor
      }

      // 如果只刷新报警星，并且没有报警，则不绘制*号
      if (this.refreshAlarmStar && !star.alarm) {
        return
      }

      // 绘制恒星*号
      this.renderer.drawAsterisk(
        angle,
        this.styles.star.starRadiusRatio,
        '*',
        starColor,
        this.styles.star.dotFont
      )

      // 使用新方法绘制恒星名称（沿半径方向）
      this.renderer.drawTextAlongRadius(
        angle,
        this.styles.star.textRadiusRatio,
        this.styles.star.textSpacing,
        star.starName,
        {
          fillStyle: fontColor,
          font: this.styles.star.starNameFont
        }
      )

      // 绘制高度信息
      if (star.horizontalAltitude !== undefined) {
        this.renderer.drawRadialText(
          angle,
          this.styles.star.textRadiusRatio - 0.05, // 稍微调整位置
          star.horizontalAltitude.toFixed(1) + '°',
          fontColor,
          '12px Arial'
        )
      }

      if (!this.refreshAlarmStar) {
        // 计算第一个字符的位置（用于悬浮检测）
        const firstCharDistance = this.renderer.radius * this.styles.star.textRadiusRatio
        const radian = CanvasUtils.degToRad(angle)
        const textCenter = {
          x: this.renderer.center.x + Math.cos(radian) * firstCharDistance,
          y: this.renderer.center.y + Math.sin(radian) * firstCharDistance
        }

        // 记录恒星位置和基本信息（用于悬浮判断）
        this.starPositions.push({
          type: 'star',
          star,
          center: textCenter,
          textCenter: textCenter,
          angle: angle, // 添加绘制角度用于排序
          radius: 20 // 悬停敏感区域半径
        })
      }
    })
  }

  /**
   * 绘制本命盘外圈（最外圈）
   * 用于黄道盘外圈绘制
   * @param {Array} natalHouses - 本命盘宫位数据
   * @param {Array} natalPlanets - 本命盘行星数据
   */
  drawNatalHouseSector (natalHouses, natalPlanets, ascDiff = 0, yellowChartSpecialPoints = {}) {
    if ((!natalHouses || natalHouses.length === 0) && (!natalPlanets || natalPlanets.length === 0)) return

    // 总经理最新确认：本命盘不再单独绘制一套后天宫。
    // 但背景盘面仍需保留，因此这里直接复用主盘后天宫绘制结果，
    // 不再使用本命盘独立宫位，避免出现不同标准的二次尺度缩放。
    const sharedHouses = Array.isArray(this.houses) && this.houses.length > 0
      ? this.houses
      : natalHouses

    for (let i = 0; i < sharedHouses.length; i++) {
      const house = sharedHouses[i]
      const nextHouse = sharedHouses[(i + 1) % sharedHouses.length]
      const startAngle = CanvasUtils.normalizeAngle(house.position)
      const endAngle = CanvasUtils.normalizeAngle(nextHouse.position)

      const styleConfig = {
        innerRadiusRatio: this.styles.natalHouseSector.innerRadiusRatio,
        outerRadiusRatio: this.styles.natalHouseSector.outerRadiusRatio,
        evenFill: i % 2 === 0 ? this.styles.natalHouseSector.evenFill : this.styles.natalHouseSector.oddFill,
        strokeStyle: this.styles.natalHouseSector.strokeStyle,
        lineWidth: this.styles.natalHouseSector.lineWidth
      }
      this.drawHouseSector(startAngle, endAngle, styleConfig)
    }

    // 绘制本命盘行星（从独立的planets数组获取，与黄道盘一致）
    if (natalPlanets && natalPlanets.length > 0) {
      natalPlanets.forEach(planet => {
        // 跳过ASC和MC（它们通过drawSpecialAxis单独绘制）
        if (planet.index === 1000014 || planet.index === 1000015) return

        const pos = planet.drawPos
        if (!planet || pos === undefined || pos === null) {
          return
        }

        const angle = CanvasUtils.normalizeAngle(pos)
        const displayName = planet.shortName || planet.name || ''
        const displayColor = planet.color || '#FFFFFF'

        // 1. 绘制行星符号（径向文字）
        const symbolPos = this.renderer.drawRadialText(
          angle,
          this.styles.natalHouseSector.textRadiusRatio,
          displayName,
          displayColor,
          this.styles.natalHouseSector.planetNameFont
        )

        // 2. 绘制行星位置点
        this.renderer.drawLabeledCircle(
          angle,
          this.styles.natalHouseSector.planetRadiusRatio,
          displayColor,
          this.styles.planetDot.radius
        )

        // 简化的本命盘行星展示，去除了速度、度、分、星座名和逆行标志的绘制

        // 记录本命盘行星位置（用于悬浮框检测）
        this.planetPositionsLookup.push({
          type: 'planet',
          data: planet,
          center: symbolPos,
          angle: angle,
          radius: 30
        })
      })
    }

    // 绘制本命盘的 ASC/MC 轴线（只画线条，不画文字）
    if (yellowChartSpecialPoints) {
      // 总经理最新确认：无论哪种绘制模式，本命盘 ASC 都必须按
      // “本命盘 ASC 绝对黄经 - 主盘 ASC 绝对黄经”来绘制。
      // 这个值应直接来自共享黄经轴预计算后的 specialPoints.ASC，
      // 与本命盘 houses / 宫头位置无关。
      const natalAscAngle = CanvasUtils.normalizeAngle(
        yellowChartSpecialPoints.ASC !== undefined ? yellowChartSpecialPoints.ASC : (0 + ascDiff)
      )
      // 总经理最新确认：无论哪种绘制模式，本命盘 MC 都必须严格相对
      // 本命盘 ASC 成 90°，不再直接依赖本命盘独立宫头或旧 MC 显示值。
      const natalMcAngle = CanvasUtils.normalizeAngle(natalAscAngle - 90)

      // 绘制 ASC 轴线（红线）
      if (natalAscAngle !== undefined && natalAscAngle !== null) {
        const natalAscAxisPos = this.renderer.drawAxisArrow(
          natalAscAngle,
          this.styles.axisArrow.hcolor,
          this.styles.axisArrow.lineWidth,
          this.styles.natalHouseSector.outerRadiusRatio,
          this.styles.natalHouseSector.innerRadiusRatio,
          false
        )

        // 计算ASC位置用于悬浮检测
        const natalAscPos = CanvasUtils.getPoint(
          CanvasUtils.normalizeAngle(natalAscAngle),
          this.renderer.radius * 1.2,
          this.renderer.center,
          this.renderer.zeroAngle
        )

        this.specialAxisPositions.push(
          { type: 'NatalASC', center: natalAscPos, angle: natalAscAngle, radius: 30 },
          { type: 'NatalASC', center: natalAscAxisPos.center, angle: natalAscAngle, rectWidth: 120, rectLength: 12 }
        )
      }

      // 绘制 MC 轴线（蓝线）- 等分宫位模式下严格相对本命盘 ASC 画 90°；标准模式继续兼容 MC/ARMC 原值
      if (natalMcAngle !== undefined && natalMcAngle !== null) {
        const natalMcAxisPos = this.renderer.drawAxisArrow(
          natalMcAngle,
          this.styles.axisArrow.vcolor,
          this.styles.axisArrow.lineWidth,
          this.styles.natalHouseSector.outerRadiusRatio,
          this.styles.natalHouseSector.innerRadiusRatio,
          false
        )

        // 计算MC位置用于悬浮检测
        const natalMcPos = CanvasUtils.getPoint(
          CanvasUtils.normalizeAngle(natalMcAngle),
          this.renderer.radius * 1.2,
          this.renderer.center,
          this.renderer.zeroAngle
        )

        this.specialAxisPositions.push(
          { type: 'NatalMC', center: natalMcPos, angle: natalMcAngle, radius: 30 },
          { type: 'NatalMC', center: natalMcAxisPos.center, angle: natalMcAngle, rectWidth: 120, rectLength: 12 }
        )
      }
    }
  }

  /**
   * 绘制天运圆点
   * @param {Array} tianyunPositions - 天运数据数组
   * @returns {Array} 天运位置信息
   */
  drawTianyun (tianyunPositions) {
    this.tianyunPositions = []
    if (!tianyunPositions || tianyunPositions.length === 0) return

    // 天运点位置在先天宫内圈往外偏移 0.01 的位置
    const radius = this.renderer.radius * (this.styles.signCircle.innerRadiusRatio + 0.015)

    tianyunPositions.forEach(item => {
      if (item.drawPos === undefined || item.drawPos === null) return

      const angle = CanvasUtils.normalizeAngle(item.drawPos)
      const center = CanvasUtils.getPoint(
        angle,
        radius,
        this.renderer.center,
        this.renderer.zeroAngle
      )

      // 绘制天运圆点 (6px)
      this.renderer.ctx.beginPath()
      this.renderer.ctx.arc(center.x, center.y, 4.0, 0, Math.PI * 2) // 半径4.0px，直径8px
      this.renderer.ctx.fillStyle = item.color || '#000'
      this.renderer.ctx.fill()
      this.renderer.ctx.strokeStyle = '#fff' // 加个白边更好看
      this.renderer.ctx.lineWidth = 0.5
      this.renderer.ctx.stroke()

      // 记录位置用于悬停检测
      this.tianyunPositions.push({
        type: 'tianyun',
        data: item,
        center: center,
        angle: angle,
        radius: 8 // 略大于点的半径，方便触发
      })
    })
  }
}
